import React, { useMemo, useState } from 'react';
import {
  Award,
  BriefcaseBusiness,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Clock3,
  FileText,
  Filter,
  LayoutDashboard,
  ListChecks,
  Search,
  ShieldCheck,
  UserPlus,
  UserSquare,
  Users,
} from 'lucide-react';
import '../Employees/Employees.css';

const statusClass = (value) => String(value || 'Open').toLowerCase().replace(/\s+/g, '-');

const PAGE_CONFIGS = {
  dashboard: {
    icon: LayoutDashboard,
    title: 'Recruitment Dashboard',
    description: 'Monitoring kebutuhan tenaga kerja, pipeline kandidat, proses seleksi, dan hiring approval.',
    tableTitle: 'Recruitment Pipeline',
    searchPlaceholder: 'Search request, vacancy, candidate, department...',
    summary: [
      { label: 'Open requests', icon: ClipboardList, tone: 'teal', valueKey: 'open' },
      { label: 'Active vacancies', icon: BriefcaseBusiness, tone: 'blue', valueKey: 'vacancies' },
      { label: 'Candidates', icon: Users, tone: 'purple', valueKey: 'candidates' },
      { label: 'Pending approval', icon: ShieldCheck, tone: 'orange', valueKey: 'pending' },
    ],
    columns: [
      { key: 'name', label: 'Pipeline item', primary: true },
      { key: 'department', label: 'Department' },
      { key: 'owner', label: 'Owner' },
      { key: 'stage', label: 'Stage' },
      { key: 'status', label: 'Status', type: 'status' },
      { key: 'description', label: 'Notes' },
    ],
    rows: [
      { id: 'REC-DASH-01', name: 'Senior IT Auditor Hiring', department: 'IT Audit', owner: 'HR Recruitment', stage: 'Selection', status: 'In Progress', description: '3 kandidat masuk tahap technical interview.' },
      { id: 'REC-DASH-02', name: 'Project Coordinator Replacement', department: 'Operations', owner: 'HR Recruitment', stage: 'Approval', status: 'Pending', description: 'Menunggu approval N+1 untuk job offering.' },
      { id: 'REC-DASH-03', name: 'Business Analyst Pool', department: 'Business Assurance', owner: 'HR Recruitment', stage: 'Vacancy', status: 'Open', description: 'Vacancy aktif untuk kebutuhan project Q3.' },
      { id: 'REC-DASH-04', name: 'Junior Auditor Intake', department: 'Sustainability', owner: 'HR Recruitment', stage: 'Pre-Onboarding', status: 'Ready', description: 'Kandidat sudah menerima offering.' },
    ],
  },
  'manpower-request': {
    icon: ClipboardList,
    title: 'Manpower Request',
    description: 'Daftar permintaan kebutuhan tenaga kerja dari department atau hiring manager.',
    tableTitle: 'Manpower Requests',
    searchPlaceholder: 'Search request, requester, department...',
    summary: [
      { label: 'Total requests', icon: ClipboardList, tone: 'teal', valueKey: 'total' },
      { label: 'Approved', icon: ShieldCheck, tone: 'blue', valueKey: 'approved' },
      { label: 'Pending review', icon: Clock3, tone: 'purple', valueKey: 'pending' },
      { label: 'Urgent requests', icon: Award, tone: 'orange', valueKey: 'urgent' },
    ],
    columns: [
      { key: 'name', label: 'Request', primary: true },
      { key: 'department', label: 'Department' },
      { key: 'requester', label: 'Requester' },
      { key: 'headcount', label: 'Headcount', type: 'count' },
      { key: 'status', label: 'Status', type: 'status' },
      { key: 'description', label: 'Reason' },
    ],
    rows: [
      { id: 'MPR-2026-001', name: 'Senior IT Auditor', department: 'IT Audit', requester: 'Edwin Chaidir', headcount: 2, status: 'Approved', priority: 'Urgent', description: 'Tambahan resource untuk project audit Q3.' },
      { id: 'MPR-2026-002', name: 'Project Coordinator', department: 'Operations', requester: 'Fauzi Rizalludin', headcount: 1, status: 'Pending', priority: 'Normal', description: 'Replacement untuk support scheduling project.' },
      { id: 'MPR-2026-003', name: 'Business Analyst', department: 'Business Assurance', requester: 'Helda Mutiara', headcount: 1, status: 'Draft', priority: 'Normal', description: 'Support mapping process dan documentation.' },
    ],
  },
  'job-requisition': {
    icon: BriefcaseBusiness,
    title: 'Job Requisition',
    description: 'Formalisasi kebutuhan posisi setelah manpower request disetujui.',
    tableTitle: 'Job Requisitions',
    searchPlaceholder: 'Search requisition, position, hiring manager...',
    summary: [
      { label: 'Requisitions', icon: BriefcaseBusiness, tone: 'teal', valueKey: 'total' },
      { label: 'Open', icon: ListChecks, tone: 'blue', valueKey: 'open' },
      { label: 'Assigned HR', icon: UserSquare, tone: 'purple', valueKey: 'owners' },
      { label: 'Need approval', icon: ShieldCheck, tone: 'orange', valueKey: 'pending' },
    ],
    columns: [
      { key: 'name', label: 'Position', primary: true },
      { key: 'department', label: 'Department' },
      { key: 'hiringManager', label: 'Hiring manager' },
      { key: 'targetDate', label: 'Target date' },
      { key: 'status', label: 'Status', type: 'status' },
      { key: 'description', label: 'Notes' },
    ],
    rows: [
      { id: 'JR-2026-001', name: 'Senior IT Auditor', department: 'IT Audit', hiringManager: 'Edwin Chaidir', targetDate: '15 Aug 2026', status: 'Open', description: 'Job description sudah lengkap dan siap dipublish.' },
      { id: 'JR-2026-002', name: 'Project Coordinator', department: 'Operations', hiringManager: 'Fauzi Rizalludin', targetDate: '31 Aug 2026', status: 'Pending', description: 'Menunggu finalisasi budget dan grade.' },
      { id: 'JR-2026-003', name: 'Business Analyst', department: 'Business Assurance', hiringManager: 'Helda Mutiara', targetDate: '10 Sep 2026', status: 'Draft', description: 'Draft requirement sedang dilengkapi.' },
    ],
  },
  'job-vacancies': {
    icon: ListChecks,
    title: 'Job Vacancies',
    description: 'Publikasi lowongan, channel sourcing, dan status vacancy.',
    tableTitle: 'Vacancy List',
    searchPlaceholder: 'Search vacancy, channel, department...',
    summary: [
      { label: 'Vacancies', icon: ListChecks, tone: 'teal', valueKey: 'total' },
      { label: 'Published', icon: ShieldCheck, tone: 'blue', valueKey: 'published' },
      { label: 'Candidates received', icon: Users, tone: 'purple', valueKey: 'candidates' },
      { label: 'Closing soon', icon: CalendarDays, tone: 'orange', valueKey: 'closing' },
    ],
    columns: [
      { key: 'name', label: 'Vacancy', primary: true },
      { key: 'department', label: 'Department' },
      { key: 'channel', label: 'Channel' },
      { key: 'candidates', label: 'Candidates', type: 'count' },
      { key: 'status', label: 'Status', type: 'status' },
      { key: 'description', label: 'Closing date' },
    ],
    rows: [
      { id: 'VAC-001', name: 'Senior IT Auditor', department: 'IT Audit', channel: 'LinkedIn, Referral', candidates: 12, status: 'Published', description: 'Close on 10 Aug 2026.' },
      { id: 'VAC-002', name: 'Project Coordinator', department: 'Operations', channel: 'Website', candidates: 5, status: 'Published', description: 'Close on 18 Aug 2026.' },
      { id: 'VAC-003', name: 'Business Analyst', department: 'Business Assurance', channel: 'Internal Pool', candidates: 3, status: 'Draft', description: 'Belum dipublish.' },
    ],
  },
  candidates: {
    icon: Users,
    title: 'Candidates',
    description: 'Daftar kandidat, sumber kandidat, posisi yang dilamar, dan status pipeline.',
    tableTitle: 'Candidate List',
    searchPlaceholder: 'Search candidate, position, source...',
    summary: [
      { label: 'Candidates', icon: Users, tone: 'teal', valueKey: 'total' },
      { label: 'Screening', icon: ListChecks, tone: 'blue', valueKey: 'screening' },
      { label: 'Interview', icon: UserSquare, tone: 'purple', valueKey: 'interview' },
      { label: 'Offered', icon: Award, tone: 'orange', valueKey: 'offered' },
    ],
    columns: [
      { key: 'name', label: 'Candidate', primary: true },
      { key: 'position', label: 'Position' },
      { key: 'source', label: 'Source' },
      { key: 'stage', label: 'Stage' },
      { key: 'status', label: 'Status', type: 'status' },
      { key: 'description', label: 'Notes' },
    ],
    rows: [
      { id: 'CND-001', name: 'Alya Ramadhani', position: 'Senior IT Auditor', source: 'Referral', stage: 'Technical Interview', status: 'Interview', description: 'Interview dengan hiring manager dijadwalkan minggu ini.' },
      { id: 'CND-002', name: 'Bima Prasetyo', position: 'Project Coordinator', source: 'LinkedIn', stage: 'HR Screening', status: 'Screening', description: 'Menunggu konfirmasi availability.' },
      { id: 'CND-003', name: 'Citra Anggraini', position: 'Business Analyst', source: 'Website', stage: 'Document Review', status: 'Screening', description: 'CV dan portfolio sedang direview.' },
      { id: 'CND-004', name: 'Dimas Putra', position: 'Junior Auditor', source: 'Campus Hiring', stage: 'Offering', status: 'Offered', description: 'Offering letter sudah dikirim.' },
    ],
  },
  'selection-process': {
    icon: ShieldCheck,
    title: 'Selection Process',
    description: 'Monitoring proses seleksi dari screening, interview, assessment, hingga decision.',
    tableTitle: 'Selection Pipeline',
    searchPlaceholder: 'Search candidate, step, assessor...',
    summary: [
      { label: 'Selection items', icon: ShieldCheck, tone: 'teal', valueKey: 'total' },
      { label: 'In progress', icon: Clock3, tone: 'blue', valueKey: 'inProgress' },
      { label: 'Passed', icon: Award, tone: 'purple', valueKey: 'passed' },
      { label: 'Need decision', icon: ListChecks, tone: 'orange', valueKey: 'pending' },
    ],
    columns: [
      { key: 'name', label: 'Candidate', primary: true },
      { key: 'position', label: 'Position' },
      { key: 'step', label: 'Current step' },
      { key: 'assessor', label: 'Assessor' },
      { key: 'status', label: 'Status', type: 'status' },
      { key: 'description', label: 'Schedule' },
    ],
    rows: [
      { id: 'SEL-001', name: 'Alya Ramadhani', position: 'Senior IT Auditor', step: 'Technical Interview', assessor: 'Edwin Chaidir', status: 'In Progress', description: '29 Jul 2026, 10:00.' },
      { id: 'SEL-002', name: 'Bima Prasetyo', position: 'Project Coordinator', step: 'HR Interview', assessor: 'HR Recruitment', status: 'In Progress', description: '30 Jul 2026, 14:00.' },
      { id: 'SEL-003', name: 'Dimas Putra', position: 'Junior Auditor', step: 'Final Decision', assessor: 'Helda Mutiara', status: 'Passed', description: 'Decision completed.' },
    ],
  },
  'job-offering': {
    icon: FileText,
    title: 'Job Offering',
    description: 'Pengelolaan offering letter, kompensasi, benefit, dan acceptance kandidat.',
    tableTitle: 'Offering List',
    searchPlaceholder: 'Search candidate, position, offer status...',
    summary: [
      { label: 'Offers', icon: FileText, tone: 'teal', valueKey: 'total' },
      { label: 'Accepted', icon: ShieldCheck, tone: 'blue', valueKey: 'accepted' },
      { label: 'Pending response', icon: Clock3, tone: 'purple', valueKey: 'pending' },
      { label: 'Need revision', icon: Award, tone: 'orange', valueKey: 'review' },
    ],
    columns: [
      { key: 'name', label: 'Candidate', primary: true },
      { key: 'position', label: 'Position' },
      { key: 'grade', label: 'Grade' },
      { key: 'sentDate', label: 'Sent date' },
      { key: 'status', label: 'Status', type: 'status' },
      { key: 'description', label: 'Notes' },
    ],
    rows: [
      { id: 'OFR-001', name: 'Dimas Putra', position: 'Junior Auditor', grade: 'L1', sentDate: '22 Jul 2026', status: 'Accepted', description: 'Kandidat sudah konfirmasi bergabung.' },
      { id: 'OFR-002', name: 'Alya Ramadhani', position: 'Senior IT Auditor', grade: 'L3', sentDate: 'Draft', status: 'Draft', description: 'Menunggu approval salary range.' },
      { id: 'OFR-003', name: 'Bima Prasetyo', position: 'Project Coordinator', grade: 'L2', sentDate: '24 Jul 2026', status: 'Pending', description: 'Menunggu response kandidat.' },
    ],
  },
  'hiring-approval': {
    icon: Award,
    title: 'Hiring Approval',
    description: 'Approval final sebelum kandidat masuk tahap onboarding.',
    tableTitle: 'Hiring Approvals',
    searchPlaceholder: 'Search approval, candidate, approver...',
    summary: [
      { label: 'Approval items', icon: Award, tone: 'teal', valueKey: 'total' },
      { label: 'Approved', icon: ShieldCheck, tone: 'blue', valueKey: 'approved' },
      { label: 'Waiting N+1', icon: Clock3, tone: 'purple', valueKey: 'pending' },
      { label: 'Rejected', icon: FileText, tone: 'orange', valueKey: 'rejected' },
    ],
    columns: [
      { key: 'name', label: 'Candidate', primary: true },
      { key: 'position', label: 'Position' },
      { key: 'approver', label: 'Approver' },
      { key: 'level', label: 'Approval level' },
      { key: 'status', label: 'Status', type: 'status' },
      { key: 'description', label: 'Notes' },
    ],
    rows: [
      { id: 'HAP-001', name: 'Dimas Putra', position: 'Junior Auditor', approver: 'Helda Mutiara', level: 'N+1', status: 'Approved', description: 'Approved untuk onboarding batch Agustus.' },
      { id: 'HAP-002', name: 'Bima Prasetyo', position: 'Project Coordinator', approver: 'Fauzi Rizalludin', level: 'N+1', status: 'Pending', description: 'Menunggu review compensation.' },
      { id: 'HAP-003', name: 'Alya Ramadhani', position: 'Senior IT Auditor', approver: 'Edwin Chaidir', level: 'N+1', status: 'Draft', description: 'Belum diajukan.' },
    ],
  },
  'pre-onboarding': {
    icon: UserSquare,
    title: 'Pre-Onboarding',
    description: 'Persiapan onboarding kandidat yang sudah menerima offering.',
    tableTitle: 'Pre-Onboarding Checklist',
    searchPlaceholder: 'Search candidate, checklist, owner...',
    summary: [
      { label: 'New joiners', icon: UserPlus, tone: 'teal', valueKey: 'total' },
      { label: 'Ready', icon: ShieldCheck, tone: 'blue', valueKey: 'ready' },
      { label: 'Pending document', icon: FileText, tone: 'purple', valueKey: 'pending' },
      { label: 'Start this month', icon: CalendarDays, tone: 'orange', valueKey: 'monthly' },
    ],
    columns: [
      { key: 'name', label: 'Candidate', primary: true },
      { key: 'position', label: 'Position' },
      { key: 'startDate', label: 'Start date' },
      { key: 'owner', label: 'Owner' },
      { key: 'status', label: 'Status', type: 'status' },
      { key: 'description', label: 'Checklist' },
    ],
    rows: [
      { id: 'ONB-001', name: 'Dimas Putra', position: 'Junior Auditor', startDate: '01 Aug 2026', owner: 'HR Operations', status: 'Ready', description: 'Account, document, and induction schedule ready.' },
      { id: 'ONB-002', name: 'Maya Sekar', position: 'Admin Project', startDate: '05 Aug 2026', owner: 'HR Operations', status: 'Pending', description: 'Menunggu dokumen NPWP dan rekening.' },
      { id: 'ONB-003', name: 'Rafi Hidayat', position: 'Auditor', startDate: '15 Aug 2026', owner: 'HR Operations', status: 'Draft', description: 'Checklist belum lengkap.' },
    ],
  },
  'recruitment-reports': {
    icon: BarChartIcon,
    title: 'Recruitment Reports',
    description: 'Laporan funnel recruitment, SLA hiring, source effectiveness, dan approval status.',
    tableTitle: 'Report Library',
    searchPlaceholder: 'Search report, owner, period...',
    summary: [
      { label: 'Reports', icon: FileText, tone: 'teal', valueKey: 'total' },
      { label: 'Monthly reports', icon: CalendarDays, tone: 'blue', valueKey: 'monthly' },
      { label: 'Ready', icon: ShieldCheck, tone: 'purple', valueKey: 'ready' },
      { label: 'Need refresh', icon: Clock3, tone: 'orange', valueKey: 'pending' },
    ],
    columns: [
      { key: 'name', label: 'Report', primary: true },
      { key: 'period', label: 'Period' },
      { key: 'owner', label: 'Owner' },
      { key: 'frequency', label: 'Frequency' },
      { key: 'status', label: 'Status', type: 'status' },
      { key: 'description', label: 'Purpose' },
    ],
    rows: [
      { id: 'RCR-001', name: 'Recruitment Funnel', period: 'Jul 2026', owner: 'HR Recruitment', frequency: 'Monthly', status: 'Ready', description: 'Konversi kandidat dari sourcing sampai hired.' },
      { id: 'RCR-002', name: 'Hiring SLA', period: 'Jul 2026', owner: 'HR Recruitment', frequency: 'Monthly', status: 'Ready', description: 'Durasi proses per vacancy dan department.' },
      { id: 'RCR-003', name: 'Source Effectiveness', period: 'Q3 2026', owner: 'HR Recruitment', frequency: 'Quarterly', status: 'Draft', description: 'Efektivitas channel sourcing kandidat.' },
    ],
  },
};

function BarChartIcon(props) {
  return <ListChecks {...props} />;
}

const uniqueCount = (rows, key) => new Set(rows.map((row) => row[key]).filter(Boolean)).size;

const resolveSummaryValue = (rows, key) => {
  if (key === 'total') return rows.length;
  if (key === 'open') return rows.filter((row) => ['Open', 'In Progress'].includes(row.status)).length;
  if (key === 'vacancies') return rows.filter((row) => String(row.stage || '').toLowerCase().includes('vacancy')).length || rows.length;
  if (key === 'candidates') return rows.reduce((sum, row) => sum + Number(row.candidates || 0), 0) || rows.length;
  if (key === 'pending') return rows.filter((row) => ['Pending', 'Draft'].includes(row.status)).length;
  if (key === 'approved') return rows.filter((row) => ['Approved', 'Accepted'].includes(row.status)).length;
  if (key === 'urgent') return rows.filter((row) => row.priority === 'Urgent').length;
  if (key === 'owners') return uniqueCount(rows, 'owner') || uniqueCount(rows, 'hiringManager');
  if (key === 'published') return rows.filter((row) => row.status === 'Published').length;
  if (key === 'closing') return rows.filter((row) => String(row.description || '').toLowerCase().includes('close')).length;
  if (key === 'screening') return rows.filter((row) => row.status === 'Screening').length;
  if (key === 'interview') return rows.filter((row) => row.status === 'Interview').length;
  if (key === 'offered') return rows.filter((row) => row.status === 'Offered').length;
  if (key === 'inProgress') return rows.filter((row) => row.status === 'In Progress').length;
  if (key === 'passed') return rows.filter((row) => row.status === 'Passed').length;
  if (key === 'accepted') return rows.filter((row) => row.status === 'Accepted').length;
  if (key === 'review') return rows.filter((row) => row.status === 'Review').length;
  if (key === 'rejected') return rows.filter((row) => row.status === 'Rejected').length;
  if (key === 'ready') return rows.filter((row) => row.status === 'Ready').length;
  if (key === 'monthly') return rows.filter((row) => row.frequency === 'Monthly' || String(row.startDate || '').includes('Aug 2026')).length;
  return 0;
};

function Status({ value }) {
  return <span className={`employee-status ${statusClass(value)}`}><i />{value}</span>;
}

function Cell({ row, column, Icon }) {
  const value = row[column.key] || '—';
  if (column.primary) {
    return (
      <div className="name-cell">
        <span className="department-avatar"><Icon size={17} /></span>
        <span><strong>{value}</strong><small>{row.id}</small></span>
      </div>
    );
  }
  if (column.type === 'status') return <Status value={value} />;
  if (column.type === 'count') return <span className="assignment-count"><Users size={15} /> {value}</span>;
  return value;
}

const DummyRecruitmentManagementPage = ({ pageKey = 'dashboard' }) => {
  const config = PAGE_CONFIGS[pageKey] || PAGE_CONFIGS.dashboard;
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('All statuses');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(7);
  const Icon = config.icon;
  const rows = config.rows;
  const statuses = [...new Set(rows.map((row) => row.status))];

  const filtered = useMemo(() => rows.filter((row) => {
    const haystack = Object.values(row).join(' ').toLowerCase();
    return (!query || haystack.includes(query.toLowerCase()))
      && (status === 'All statuses' || row.status === status);
  }), [query, rows, status]);

  const visible = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const activeFilters = status !== 'All statuses' ? 1 : 0;

  const reset = () => {
    setQuery('');
    setStatus('All statuses');
    setPage(1);
  };

  return (
    <div className="employee-directory-page hris-master-page">
      <header className="employee-page-header">
        <div>
          <div className="eyebrow"><Icon size={15} /> RECRUITMENT MANAGEMENT</div>
          <h1>{config.title}</h1>
          <p>{config.description}</p>
        </div>
        <div className="source-state">
          <span><i /> Mock data</span>
          <small>Belum tersinkron ke backend HRIS.</small>
        </div>
      </header>

      <section className="employee-summary-grid">
        {config.summary.map((item) => {
          const SummaryIcon = item.icon;
          return (
            <article key={item.label}>
              <span className={`summary-icon ${item.tone}`}><SummaryIcon size={21} /></span>
              <div><small>{item.label}</small><strong>{resolveSummaryValue(rows, item.valueKey)}</strong></div>
            </article>
          );
        })}
      </section>

      <section className="employee-filter-panel">
        <div className="filter-panel-head">
          <div>
            <h2>{config.tableTitle}</h2>
            <p>Gunakan pencarian dan filter untuk menemukan data recruitment yang sesuai.</p>
          </div>
          <button className="filter-toggle" type="button">
            <Filter size={16} /> Filters {activeFilters > 0 && <b>{activeFilters}</b>}
          </button>
        </div>
        <div className="search-row">
          <label className="employee-search">
            <Search size={18} />
            <input
              value={query}
              onChange={(event) => { setQuery(event.target.value); setPage(1); }}
              placeholder={config.searchPlaceholder}
            />
          </label>
          <label className="hris-inline-select">
            <select value={status} onChange={(event) => { setStatus(event.target.value); setPage(1); }}>
              <option>All statuses</option>
              {statuses.map((value) => <option key={value}>{value}</option>)}
            </select>
          </label>
          <button className="clear-filter" onClick={reset}>Clear</button>
        </div>
      </section>

      <section className="employee-table-panel">
        <div className="directory-heading">
          <div>
            <h2>{config.tableTitle}</h2>
            <p>{filtered.length} record{filtered.length === 1 ? '' : 's'} shown</p>
          </div>
          <span className="sync-note"><Clock3 size={15} /> Mock data for front-end preview</span>
        </div>
        <div className="table-scroll">
          <table className="employee-table hris-master-table hris-dummy-table">
            <thead>
              <tr>{config.columns.map((column) => <th key={column.key}>{column.label}</th>)}</tr>
            </thead>
            <tbody>
              {visible.length ? visible.map((row) => (
                <tr key={row.id}>
                  {config.columns.map((column) => (
                    <td key={`${row.id}-${column.key}`}>
                      <Cell row={row} column={column} Icon={Icon} />
                    </td>
                  ))}
                </tr>
              )) : (
                <tr><td colSpan={config.columns.length} className="empty-state">Tidak ada data yang sesuai dengan filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <footer className="table-footer">
          <span>Showing {filtered.length ? ((page - 1) * rowsPerPage) + 1 : 0}-{Math.min(page * rowsPerPage, filtered.length)} of {filtered.length} records</span>
          <div className="pagination-controls">
            <label>Rows per page<select value={rowsPerPage} onChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(1); }}><option value={5}>5</option><option value={7}>7</option><option value={10}>10</option><option value={25}>25</option></select></label>
            <div className="pager">
              <button disabled={page === 1} onClick={() => setPage((value) => value - 1)} aria-label="Previous page"><ChevronLeft size={18} /></button>
              <b>{page}</b>
              <button disabled={page === totalPages} onClick={() => setPage((value) => value + 1)} aria-label="Next page"><ChevronRight size={18} /></button>
            </div>
          </div>
        </footer>
      </section>
    </div>
  );
};

export default DummyRecruitmentManagementPage;
